// ═══ source.io — lib.ts ═══
// Shared pure library: IDs & URL management, core types, sanitization &
// domain constants, COLOR_THEMES, SquareData/AppSettings, timezones &
// timestamp formatting. NO React/JSX here — keep this file dependency-free.
// Extracted from App.tsx sections 2, 3, 5, 7, 9.

// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ▓▓▓▓▓ IDS & URL MANAGEMENT ▓▓▓▓▓
// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ── ID generation ────────────────────────────────────────────────────────────
// Stored in DB / sent to API: 8-char alphanumeric, first char is type prefix
//   b + 7 random chars  →  "bAB3XY7Z"   (board)
//   c + 7 random chars  →  "cAB3XY7Z"   (chain)
// ID format: prefix + 4 digits + 4 letters, always lowercase
// Stored (no hyphen): b1234abcd  (9 chars for b/c/s, 10 for sn)
// Displayed:          b-1234-abcd  (with dash between digits and letters)
export const _ID_LETTERS = 'abcdefghjkmnpqrstuvwxyz' as const; // lowercase, no ambiguous i/l/o
export const _ID_DIGITS  = '23456789' as const;                // no 0/1
export const _rand = (s: string) => s[Math.floor(Math.random() * s.length)];
// Generate: 4 random digits + 4 random letters, in that fixed order
export const _mkId = (prefix: string): string =>
  `${prefix}${_rand(_ID_DIGITS)}${_rand(_ID_DIGITS)}${_rand(_ID_DIGITS)}${_rand(_ID_DIGITS)}${_rand(_ID_LETTERS)}${_rand(_ID_LETTERS)}${_rand(_ID_LETTERS)}${_rand(_ID_LETTERS)}`;

export const generateBoardId  = (): string => _mkId('b');
export const generateChainId  = (): string => _mkId('c');
export const generateSourceId = (): string => _mkId('s');
// Seed node (top / main source of a tree): "sn" prefix → stored as "sn1234abcd" (10 chars)
export const generateSeedId = (): string =>
  `sn${_rand(_ID_DIGITS)}${_rand(_ID_DIGITS)}${_rand(_ID_DIGITS)}${_rand(_ID_DIGITS)}${_rand(_ID_LETTERS)}${_rand(_ID_LETTERS)}${_rand(_ID_LETTERS)}${_rand(_ID_LETTERS)}`;

// normId: strip ALL hyphens, lowercase  →  safe for DB / API routes
export const normId = (id: string): string => id.toLowerCase().replace(/-/g, '');
// dispId: insert dashes → b-1234-abcd, sn-1234-abcd
//   b1234abcd  →  b-1234-abcd
//   sn1234abcd →  sn-1234-abcd
export const dispId = (id: string): string => {
  const s = id.toLowerCase().replace(/-/g, '');
  if (s.startsWith('sn') && s.length === 10) return `sn-${s.slice(2, 6)}-${s.slice(6)}`;
  if (s.length === 9 && /^[bcse]/.test(s)) return `${s[0]}-${s.slice(1, 5)}-${s.slice(5)}`;
  // Fallback for any other format
  if (s.startsWith('sn') && s.length >= 3) return `sn-${s.slice(2)}`;
  return s.length >= 2 ? `${s[0]}-${s.slice(1)}` : s;
};

// Prefix guards (work with 0, 1, or 2 hyphens)
export const _isBId  = (s: string) => /^b-?/i.test(s)  && s.replace(/-/g,'').length === 9;
export const _isCId  = (s: string) => /^c-?/i.test(s)  && s.replace(/-/g,'').length === 9;
export const _isSId  = (s: string) => /^s-?/i.test(s)  && !s.replace(/-/g,'').startsWith('sn') && s.replace(/-/g,'').length === 9;
export const _isSnId = (s: string) => /^sn-?/i.test(s) && s.replace(/-/g,'').length === 10;

// ── URL management ───────────────────────────────────────────────────────────
// URL scheme (simplified — each ID is self-resolving):
//   /                          → overview / dashboard
//   /about/ /tos/ /gdpr/ /optout/ → static corporate pages
//   /b-xxx/                    → board (zoom to fit all)
//   /s-xxx/                    → source (zoom to 100%, center)
//   /c-xxx/                    → chain (resolves to board)
//   /sn-xxx/                   → seed node (resolves to board, centers)
//   /b-xxx/c-xxx/              → legacy chain view (still supported)
//   /b-xxx/c-xxx/s-xxx/        → legacy source in chain
//   /b-xxx/sn-xxx/             → legacy seed node
export type _ViewMode = 'overview' | 'chain' | 'source';
export type _StaticPage = 'about' | 'tos' | 'gdpr' | 'optout' | null;
export interface _URLState {
  boardId:    string | null;
  chainId:    string | null;
  sourceId:   string | null;
  tab:        string | null;
  viewMode:   _ViewMode;
  staticPage: _StaticPage;
  /** Frozen-snapshot segment, e.g. 'freezed-2026-06-02-2003z' from
      /b-2767-xxtx/freezed-2026-06-02-2003Z/. Null for live boards. */
  freezeKey?: string | null;
}

// ── chainId↔boardId localStorage cache ───────────────────────────────────────
export const _cacheChain = (chainId: string, boardId: string) => {
  try { localStorage.setItem('_cid_' + normId(chainId), normId(boardId)); } catch {}
};
export const _lookupChain = (chainId: string): string | null => {
  try { return localStorage.getItem('_cid_' + normId(chainId)); } catch { return null; }
};

export const updateURL = (
  boardId:  string,
  chainId?:  string | null,
  sourceId?: string | null,
  tab?:      string | null,
): void => {
  if (chainId && boardId) _cacheChain(chainId, boardId);
  let path = '/';
  // Prefer the most specific ID as a standalone segment
  if (sourceId) {
    path = `/${dispId(sourceId)}/`;
    if (tab && ['file', 'ref', 'info'].includes(tab)) path += tab;
  } else if (chainId) {
    path = `/${dispId(chainId)}/`;
    if (tab && ['code', 'refs', 'history'].includes(tab)) path += tab;
  } else if (boardId) {
    path = `/${dispId(boardId)}/`;
    if (tab && ['code', 'refs', 'history'].includes(tab)) path += tab;
  }
  if (window.history?.replaceState) {
    try { window.history.replaceState({ boardId, chainId, sourceId, tab }, '', path); } catch { window.location.hash = path; }
  } else {
    window.location.hash = path;
  }
};

export const getURLParams = (): _URLState => {
  let path = window.location.pathname.replace(/^\//, '');
  if (!path && window.location.hash) path = window.location.hash.replace(/^#\/?/, '');
  // ── Frozen snapshot segment — /b-xxx/freezed-YYYY-MM-DD-HHMMZ/ ──
  // Pulled out before normal parsing so the rest of the path resolves the
  // board exactly as a live URL would; the key is re-attached at the end.
  let freezeKey: string | null = null;
  const fm = path.match(/(^|\/)(freezed-\d{4}-\d{2}-\d{2}-\d{4}z)(\/|$)/i);
  if (fm && fm.index !== undefined) {
    freezeKey = fm[2].toLowerCase();
    path = (path.slice(0, fm.index) + '/' + path.slice(fm.index + fm[0].length)).replace(/^\/+|\/+$/g, '').replace(/\/{2,}/g, '/');
  }
  const state = ((path: string): _URLState => {
  const empty: _URLState = { boardId: null, chainId: null, sourceId: null, tab: null, viewMode: 'overview', staticPage: null };
  if (!path) return empty;

  // Static corporate pages
  const slug = path.replace(/\/$/, '').toLowerCase();
  if (slug === 'about') return { ...empty, staticPage: 'about' };
  if (slug === 'tos')   return { ...empty, staticPage: 'tos' };
  if (slug === 'gdpr')  return { ...empty, staticPage: 'gdpr' };
  if (slug === 'optout') return { ...empty, staticPage: 'optout' };

  path = path.replace(/\/$/, '');

  // strip optional /tab suffix
  let tab: string | null = null;
  for (const t of ['file', 'ref', 'info']) {
    if (path.endsWith('/' + t)) { tab = t; path = path.slice(0, -(t.length + 1)); break; }
  }
  path = path.replace(/\/$/, '');

  const parts = path.split('/').filter(Boolean);
  if (!parts.length) return { ...empty, tab };
  const [p0, p1, p2] = parts;
  const p0l = p0.toLowerCase();
  const p1l = p1?.toLowerCase();
  const p2l = p2?.toLowerCase();

  // NEW: Single-segment URLs — /s-xxx/, /c-xxx/, /sn-xxx/
  if (parts.length === 1) {
    if (_isSId(p0l)) return { boardId: null, chainId: null, sourceId: p0l, tab, viewMode: 'source', staticPage: null };
    if (_isSnId(p0l)) return { boardId: null, chainId: null, sourceId: p0l, tab, viewMode: 'source', staticPage: null };
    if (_isCId(p0l)) return { boardId: _lookupChain(p0l) || null, chainId: p0l, sourceId: null, tab, viewMode: 'chain', staticPage: null };
    if (_isBId(p0l)) return { boardId: p0l, chainId: null, sourceId: null, tab, viewMode: 'overview', staticPage: null };
    return { ...empty, tab };
  }

  // LEGACY: Multi-segment /b-xxx/... patterns (still supported)
  if (_isBId(p0l)) {
    if (p1l && _isCId(p1l)) {
      // /b-xxx/c-xxx/ or /b-xxx/c-xxx/s-xxx/
      _cacheChain(p1l, p0l);
      if (p2l && (_isSId(p2l) || _isSnId(p2l))) return { boardId: p0l, chainId: p1l, sourceId: p2l, tab, viewMode: 'source', staticPage: null };
      return { boardId: p0l, chainId: p1l, sourceId: null, tab, viewMode: 'chain', staticPage: null };
    }
    if (p1l && (_isSId(p1l) || _isSnId(p1l))) {
      // /b-xxx/sn-xxx/ — tree seed node
      return { boardId: p0l, chainId: null, sourceId: p1l, tab, viewMode: 'source', staticPage: null };
    }
    return { boardId: p0l, chainId: null, sourceId: null, tab, viewMode: 'overview', staticPage: null };
  }
  return { ...empty, tab };
  })(path);
  return { ...state, freezeKey };
};

// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ▓▓▓▓▓ CORE TYPES ▓▓▓▓▓
// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// Core data shapes used everywhere: position/size primitives, the per-source
// `SourceFields` flat record, `ReferencePointer` for cited ranges,
// `UploadedFile` metadata, `RefPoint` for tree-mode horizontal lanes, and the
// `TrustRates` 5-tuple. `SquareData` (the full source row) lives further down
// after `FieldEdit` and is the central application type.
export interface Position { x: number; y: number; }
export interface Size { width: number; height: number; }
export interface SourceFields { field1: string; field2: string; field3: string; field4: string; field5: string; field6: string; field7: string; field8: string; field9: string; field10: string; field11: string; field12: string; field13: string; field14: string; field15: string; }
export interface ReferencePointer { from: string; to: string; notes: string; fromTime?: number; toTime?: number; refType?: string; notApplicable?: boolean; pointerType?: string;
  // Section anchors (book sources, page pointer only). Same shape as
  // the two-field model on tree-mode RefPoints — opening words on the
  // From page, closing words on the To page. Surfaced in the Set
  // Reference dialog when sourceType === 'book' and pointerType ===
  // 'page'. Both optional; either can be filled alone.
  fromSectionStartsWith?: string;
  toSectionEndsWith?: string;
}
export interface UploadedFile { name: string; size: number; type: string; uploadedAt: string; dataUrl?: string; url?: string; previewUrl?: string; refCropUrl?: string; pageCount?: number; citedPages?: number[]; refAreas?: Array<{x:number;y:number;w:number;h:number;label?:string}>; origin?: string; pageUrls?: string[]; highlights?: Array<{type:string;x1:number;y1:number;x2:number;y2:number;color:string;thickness?:number}>; htmlUrl?: string; originalUrl?: string; archivedAt?: string;
  // Audio-specific: when the source is an audio file, refTimestamp marks the
  // citation point in seconds, and audioDuration caches the total length so
  // the player UI can render the seek bar before the audio metadata loads.
  // refTimestampEnd optionally marks the end of a cited range (e.g. "from
  // 1:42 to 2:15"); when undefined, the citation is a single point.
  refTimestamp?: number; refTimestampEnd?: number; audioDuration?: number;
}
export interface RefPoint { id: string; y: number; label: string; value: string; sourceIds: string[]; fromValue?: string; toValue?: string; branchEnded?: boolean; branchEndReason?: string;
  // Optional section-anchor fields, surfaced in the RefPointDialog only
  // when the active pointer is 'page'. The cited material spans from
  // the From page to the To page; these capture the opening words on
  // the From page and the closing words on the To page so the cited
  // section is identifiable independent of any numbering scheme.
  //
  //   fromSectionStartsWith — opening words of the cited section on
  //     the From page (e.g. "It became apparent").
  //   toSectionEndsWith — closing words of the cited section on the To
  //     page (e.g. "before the final outcome.").
  //
  // Both optional; either can be filled alone. For single-page
  // citations (no To value) only fromSectionStartsWith is meaningful.
  fromSectionStartsWith?: string;
  toSectionEndsWith?: string;
}
export interface TrustRates { accuracy: number; relevance: number; credibility: number; completeness: number; recency: number; verifiability: number; }
export const API_BASE = '/api';

// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ▓▓▓▓▓ SANITIZATION & DOMAIN CONSTANTS ▓▓▓▓▓
// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// XSS-stripping for user inputs, source-box dimensions, reference type
// enumerations, and per-pointer-type input validation.

// Security: sanitize user inputs to prevent XSS/injection.
// Control + zero-width chars are stripped FIRST: browsers ignore \t \n \r
// inside a URL scheme, so "java\nscript:" bypasses a plain /javascript:/
// strip. Normalizing before the scheme strips closes that hole.
export const sanitize = (s: string): string => {
  if (!s || typeof s !== 'string') return '';
  return s
    .replace(/[\u0000-\u001F\u007F\u200B-\u200D\uFEFF]/g, '')
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/data:text\/html/gi, '');
};

// Security: render-time gate for any dynamic href / window.open target that
// carries user- or server-sourced text (profile websites, source url fields,
// archived originalUrl). Returns a normalized absolute URL only for http(s)
// and mailto; anything else (javascript:, data:, vbscript:, garbage) -> undefined.
// Input-time sanitize() is best-effort; this is the authoritative check at the
// point where a string becomes a navigable URL.
export const safeHref = (raw: string | null | undefined): string | undefined => {
  if (!raw || typeof raw !== 'string') return undefined;
  try {
    const u = new URL(raw.replace(/[\u0000-\u001F\u007F\u200B-\u200D\uFEFF]/g, '').trim(), window.location.origin);
    if (u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:') return u.href;
  } catch { /* unparseable -> not a link */ }
  return undefined;
};

// Shared fetch guard: parsed JSON on 2xx, rejection otherwise. Was defined
// identically inside two separate data-loading functions.
export const okJson = (r: Response) => r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`));

// Stable empty array for optional list props — avoids allocating a fresh []
// per render when a source has no edit-log entries.
export const EMPTY_EDIT_LOG: never[] = [];
export const sanitizeFields = (fields: SourceFields): SourceFields => {
  const result: any = {};
  for (const key of Object.keys(fields)) result[key] = sanitize((fields as any)[key] || '');
  return result as SourceFields;
};
export const isValidId = (id: string): boolean => /^[A-Za-z0-9]{4,16}$/.test(id);
export const SQUARE_WIDTH = 560, SQUARE_HEIGHT = 400;
export const MAX_SOURCES_PER_BRANCH = 12;
export const emptyTrustRates: TrustRates = { accuracy: 0, relevance: 0, credibility: 0, verifiability: 0, completeness: 0, recency: 0 };
export const REF_TYPES = ['Citation', 'Quote', 'Proof', 'Evidence', 'Paraphrase', 'Summary', 'Analysis', 'Counterpoint', 'Support', 'Context'];
// Per-pointer-type validation for ref point from/to inputs.
// Returns either a "kept" string (the validated value, possibly stripped) or an error message.
// page / pixel x-coord components → digits only; section → digits, dots, hyphens, "Ch.", spaces;
// area → digits, dots, comma, minus, space (lat,lon); datetime → digits, hyphens, colons, spaces, T;
// other → anything goes.
export const validateRefPointInput = (pointerType: string, raw: string): { value: string; error: string } => {
  const v = raw;
  switch (pointerType) {
    case 'page': {
      // Strip everything that isn't a digit. Empty is allowed (means "clear").
      const stripped = v.replace(/[^0-9]/g, '');
      const error = (v.length > 0 && stripped !== v) ? 'Page must be a number' : '';
      return { value: stripped, error };
    }
    case 'section': {
      // Allow digits, dots, hyphens, letters (for "Ch. 4"), spaces, period.
      const stripped = v.replace(/[^0-9A-Za-z.\-\s]/g, '');
      const error = (v.length > 0 && stripped !== v) ? 'Section may contain digits, letters, dots, and hyphens' : '';
      return { value: stripped, error };
    }
    case 'pixel':
    case 'area': {
      // Coordinate pair "x, y" or "lat, lon" — allow digits, dots, comma, minus, spaces.
      const stripped = v.replace(/[^0-9.,\-\s]/g, '');
      const error = (v.length > 0 && stripped !== v) ? 'Coordinates may contain digits, commas, and decimals' : '';
      return { value: stripped, error };
    }
    case 'datetime': {
      // ISO-ish date/time — allow digits, hyphens, colons, T, space, slash.
      const stripped = v.replace(/[^0-9:\-T\s\/]/g, '');
      const error = (v.length > 0 && stripped !== v) ? 'Date/time may contain digits, dashes, colons, and T' : '';
      return { value: stripped, error };
    }
    case 'time':
    case 'other':
    default:
      return { value: v, error: '' };
  }
};

export const REF_POINTER_TYPES: { key: string; label: string; icon: string; fromLabel: string; toLabel: string; placeholder?: string }[] = [
  { key: 'page', label: 'Page', icon: '📄', fromLabel: 'From Page', toLabel: 'To Page', placeholder: 'e.g. 42' },
  { key: 'section', label: 'Section', icon: '§', fromLabel: 'From Section', toLabel: 'To Section', placeholder: 'e.g. 2.3, Ch. 4' },
  { key: 'time', label: 'Time', icon: '⏱️', fromLabel: 'From', toLabel: 'To' },
  { key: 'datetime', label: 'Date/Time', icon: '📅', fromLabel: 'From Date', toLabel: 'To Date', placeholder: 'e.g. 2024-03-15 14:30' },
  { key: 'area', label: 'Area (Lat/Lon)', icon: '📍', fromLabel: 'Lat, Lon', toLabel: 'Lat, Lon (end)', placeholder: 'e.g. 48.8566, 2.3522' },
  { key: 'pixel', label: 'Pixel Area', icon: '🔲', fromLabel: 'x, y (top-left)', toLabel: 'x, y (bottom-right)', placeholder: 'e.g. 120, 80' },
  { key: 'other', label: 'Other', icon: '✏️', fromLabel: 'From', toLabel: 'To', placeholder: 'Describe location' },
];
export const DEFAULT_POINTER_TYPES: Record<string, string[]> = {
  book: ['page', 'section'],
  newspaper: ['page', 'section'],
  film: ['time'], video: ['time'], podcast: ['time'], music: ['time'], speech: ['time'], interview: ['time'],
  event: ['area', 'datetime'],
  map: ['area'],
  photograph: ['pixel', 'datetime'],
  website: ['pixel'],
  article: ['pixel', 'page', 'section'],
  'web-source': ['pixel'],
  default: ['page', 'section', 'other'],
};

// Compact label for a tree-bubble using (pointerType, fromValue, toValue).
// "page" -> "p. 33–50"; "section" -> "§ 2.1–2.4"; "time" -> "33:00–50:00"; "other"/legacy -> raw range or value
export const formatRefPointLabel = (pointerType: string | undefined, fromValue: string | undefined, toValue: string | undefined, legacy?: string): string => {
  const f = (fromValue || '').trim();
  const t = (toValue || '').trim();
  if (!f && !t) return (legacy || '').trim();
  const range = t ? `${f}–${t}` : f;
  switch (pointerType) {
    case 'page': return `p. ${range}`;
    case 'section': return `§ ${range}`;
    case 'time': return range;
    case 'datetime': return range;
    case 'area': return range;
    case 'pixel': return range;
    default: return range;
  }
};

export type ColorTheme = 'default' | 'clerk' | 'claude';
export const COLOR_THEMES: Record<ColorTheme, { label: string; primary: string; bg: string; bgDark: string; paper: string; paperDark: string; accent?: string; sidebarDark?: string; borderDark?: string }> = {
  // Light-mode palette: muted slate/greige surfaces instead of near-white.
  // bg = page background (clearly toned), paper = cards/surfaces (one step
  // lighter so elevation still reads, but never pure white).
  default: { label: 'Default', primary: '#3b82f6', bg: '#dfe3ea', bgDark: '#0a0a0f', paper: '#ebeef3', paperDark: '#1a1a24' },
  clerk: { label: 'Clerk', primary: '#6c47ff', bg: '#e2ddf0', bgDark: '#111126', paper: '#eeebf7', paperDark: '#19192f', accent: '#a78bfa', sidebarDark: '#13132b', borderDark: 'rgba(108,71,255,0.15)' },
  claude: { label: 'Claude', primary: '#d97706', bg: '#eae2d4', bgDark: '#1a1510', paper: '#f3ede2', paperDark: '#231e16', accent: '#f59e0b', sidebarDark: '#1e1912', borderDark: 'rgba(217,119,6,0.15)' },
};

// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ▓▓▓▓▓ SQUARE DATA & APP SETTINGS ▓▓▓▓▓
// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// `SquareData` is the canonical per-source record carried through every
// component (canvas board → SingleSquare → server JSON). `AppSettings`
// holds the per-user UI preferences (timezone, date format, citation
// default, trust-weight, etc.).
export interface FieldEdit { field: string; oldValue: string; newValue: string; editedAt: string; editedBy: string; }
export interface SquareData {
  id: number; sourceId: string; size: Size; position: Position; sourceType: string | null;
  name: string; fields: SourceFields; order: number; createdAt: string; updatedAt: string; createdBy: string; uploadedFile: UploadedFile | null;
  reference: ReferencePointer; isMainSource?: boolean; refPointId?: string; trustRates: TrustRates; trustAssessed: boolean; comments?: string;
  fieldEdits?: FieldEdit[]; // Track edits made after board went public
  apiPrefillFields?: Record<string, string>; // Original values from API (OpenLib, IMDB)
  // Author claim — set by the source's creator (chain owner) to indicate they
  // are the actual author of the cited work. Surfaced on the source's
  // summary/info bubble so visitors weigh the trust scoring as self-rated.
  isAuthor?: boolean;
  // Per-category visitor agree/disagree on the source's trust scoring.
  // Visitors rate the source group (credibility/recency/completeness) and
  // the reference group (accuracy/relevance) separately, since the two
  // judgments are conceptually different. Each user has up to 2 votes per
  // source — one per category.
  trustVoteSourceAgree?: number;
  trustVoteSourceDisagree?: number;
  trustVoteReferenceAgree?: number;
  trustVoteReferenceDisagree?: number;
  currentUserTrustVoteSource?: 'agree' | 'disagree' | null;
  currentUserTrustVoteReference?: 'agree' | 'disagree' | null;
}
export interface User { username: string; isLoggedIn: boolean; }
export interface AppSettings {
  timezone: string;
  dateFormat: 'YYYY-MM-DD' | 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'DD.MM.YYYY';
  timeFormat: '24h' | '12h';
  defaultCiteFormat: 'apa' | 'harvard' | 'mla' | 'chicago' | 'vancouver';
  defaultZoom: number;
  showGrid: boolean;
  snapToGrid: boolean;
  autoSave: boolean;
  language: string;
  /** Personal weight (0–100) given to the source-trust group when computing
      the combined Trust average. The reference-trust group gets `100 - n`.
      A reader who values the source's overall reliability more than the
      specific cited part can shift this to e.g. 70 (then references count
      30%); the inverse for someone caring more about the cited fragment.
      Default 50 produces an equal-weight blend, identical to the previous
      behavior for everyone who never opens settings. The setting is purely
      a local lens — server-computed public averages stay unweighted so two
      different viewers can't see different scores for the same board. */
  trustSourceWeight: number;
}
export const DEFAULT_SETTINGS: AppSettings = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateFormat: 'YYYY-MM-DD',
  timeFormat: '24h',
  defaultCiteFormat: 'apa',
  defaultZoom: 100,
  showGrid: true,
  snapToGrid: true,
  autoSave: true,
  language: 'en',
  trustSourceWeight: 50,
};

// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
// ▓▓▓▓▓ TIMEZONES & TIMESTAMP FORMATTING ▓▓▓▓▓
// ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
export const formatTimestamp = (isoDate: string, settings: AppSettings, includeTime?: boolean): string => {
  try {
    // Detect if the string contains a time component (T separator or space separator like SQLite's datetime)
    const hasTime = /T|\d{4}-\d{2}-\d{2}\s\d{2}:/.test(isoDate);
    // Normalise: SQLite uses space, JS Date needs 'T'
    const normalised = isoDate.replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:)/, '$1T$2');
    const d = new Date(hasTime ? normalised : isoDate + 'T12:00:00');
    if (isNaN(d.getTime())) return isoDate;
    const opts: Intl.DateTimeFormatOptions = { timeZone: settings.timezone, year: 'numeric', month: '2-digit', day: '2-digit' };
    const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(d);
    const y = parts.find(p => p.type === 'year')?.value || '';
    const m = parts.find(p => p.type === 'month')?.value || '';
    const da = parts.find(p => p.type === 'day')?.value || '';
    let dateStr = '';
    switch (settings.dateFormat) {
      case 'DD/MM/YYYY': dateStr = `${da}/${m}/${y}`; break;
      case 'MM/DD/YYYY': dateStr = `${m}/${da}/${y}`; break;
      case 'DD.MM.YYYY': dateStr = `${da}.${m}.${y}`; break;
      default: dateStr = `${y}-${m}-${da}`;
    }
    if (includeTime !== false && hasTime) {
      const tOpts: Intl.DateTimeFormatOptions = { timeZone: settings.timezone, hour: '2-digit', minute: '2-digit', hour12: settings.timeFormat === '12h' };
      const timeStr = new Intl.DateTimeFormat('en-US', tOpts).format(d);
      return `${dateStr} ${timeStr}`;
    }
    return dateStr;
  } catch { return isoDate; }
};
export const getNowTimestamp = (tz: string): string => {
  const opts: Intl.DateTimeFormatOptions = { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' };
  const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year')?.value || '';
  const m = parts.find(p => p.type === 'month')?.value || '';
  const d = parts.find(p => p.type === 'day')?.value || '';
  return `${y}-${m}-${d}`;
};
export const TIMEZONES = [
  'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Stockholm',
  'Europe/Moscow', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Sao_Paulo', 'Asia/Tokyo', 'Asia/Shanghai',
  'Asia/Kolkata', 'Asia/Dubai', 'Australia/Sydney', 'Pacific/Auckland',
  'Africa/Cairo', 'Africa/Johannesburg',
];
export const getUtcOffset = (tz: string): string => {
  try {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const tzStr = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false, timeZoneName: 'shortOffset' }).format(now);
    const match = tzStr.match(/GMT([+-]\d+(?::\d+)?)/);
    if (match) return match[1].includes(':') ? match[1] : match[1] + ':00';
    if (tz === 'UTC') return '+0:00';
    // Fallback: compute from date diff
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(now);
    const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    const utcParts = new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(now);
    const uh = parseInt(utcParts.find(p => p.type === 'hour')?.value || '0');
    const um = parseInt(utcParts.find(p => p.type === 'minute')?.value || '0');
    let diff = (h * 60 + m) - (uh * 60 + um);
    if (diff > 720) diff -= 1440;
    if (diff < -720) diff += 1440;
    const sign = diff >= 0 ? '+' : '-';
    const ad = Math.abs(diff);
    return `${sign}${Math.floor(ad / 60)}:${String(ad % 60).padStart(2, '0')}`;
  } catch { return ''; }
};
export const formatTzLabel = (tz: string): string => {
  const offset = getUtcOffset(tz);
  return `(UTC${offset}) ${tz.replace(/_/g, ' ')}`;
};

export const emptyFields: SourceFields = { field1: '', field2: '', field3: '', field4: '', field5: '', field6: '', field7: '', field8: '', field9: '', field10: '', field11: '', field12: '', field13: '', field14: '', field15: '' };
export const emptyReference: ReferencePointer = { from: '', to: '', notes: '', fromTime: 0, toTime: 0 };